import React, { useState, useEffect } from "react";
import "./Calendar.css";
import ReactTooltip from "react-tooltip";
import { getBookings, getCourses, getOpeningHours } from "./CalendarFunctions.js";
import { getSASToken } from "../UploadImage/AzureStorage.service.js";
import { getTherapistAzureContainer } from "../../actions/index.js";
import azureStorageEnvVar from "../../constants/AzureStorageEnvironmentVariable";
import { useHistory } from "react-router-dom";
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import daLocale from "@fullcalendar/core/locales/da";


const CalendarComponent = React.memo(props => {
    const [width, setWidth] = useState(window.innerWidth);
    const history = useHistory();
    const handleDatesSet = arg => {
        props.setCurrentViewDate(arg.start);
    };

    const fetchEvents = async (info, successCallback, failureCallback) => {
        try {
            const from = Date.parse(info.start);
            const to = Date.parse(info.end);
            const [bookings, courses, openings] = await Promise.all([
                getBookings(from, to),
                getCourses(from, to),
                getOpeningHours(from, to)
            ]);

            let result = bookings.concat(courses.flat(), openings.flat());
            result = result.map(r => {
                const fullName = [r.nameFirst, r.nameLast].filter(Boolean).join(" ");
                return {
                    ...r,
                    title: r.treatmentId ? r.title || "" : r.reason || fullName || "",
                    description: r.description || "", // Ensure it's included
                    reason: r.reason || "", // Optional: keep it explicitly
                    
                };
            });

            successCallback(result);
        } catch (error) {
            console.error("Failed to fetch events:", error);
            failureCallback(error);
        }
    };

    useEffect(() => {
        const handleResize = () => setWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [window]);

    const handleMouseOver = async (info, el) => {
        if (!info) return; // Guard clause to prevent running for hour slots or undefined events
        const { AZURE_STORAGE_URL, RESIZED_IMG_PREFIX, CLIENT_PREFIX } = azureStorageEnvVar;
        let url = "",
            imageName = "";

        if (info?.profileImage) {
            try {
                const container = await getTherapistAzureContainer(props.therapist.id);
                const SASToken = await getSASToken(container);
                url = `${AZURE_STORAGE_URL}/${container}/${CLIENT_PREFIX}_${info.clientId}/${RESIZED_IMG_PREFIX}_${info.profileImage}?${SASToken}`;
                imageName = info.profileImage;
            } catch (err) {
                console.error("Error generating image URL:", err);
            }
        }

        // Create HTML safely
        const tooltipHtml =
            info.reason && info.description
                ? `<div style="max-width: 300px;">
              <p><b>Behandling</b>: ${info.description}</p>
              <p><b>Årsag</b>: ${info.reason}</p>
              ${imageName && url ? `<img src="${url}" style="width:120px; aspect-ratio:6/7"/>` : ""}
           </div>`
                : info.reason
                ? `<div style="max-width: 300px;"><p><b>Årsag</b>: ${info.reason}</p></div>`
                : info.description
                ? `<div style="max-width: 300px;">
              <p><b>Behandling</b>: ${info.description}</p>
           </div>`
                : null;

        if (tooltipHtml) {
            el.setAttribute("data-tip", tooltipHtml);
        } else {
            el.removeAttribute("data-tip");
        }
        // ReactTooltip.show(el);
        ReactTooltip.rebuild();
    };

    const calendarProps = {
        id: "calendar",
        themeSystem: "standard",
        plugins: [interactionPlugin, dayGridPlugin, timeGridPlugin],
        initialView: width >= 810 ? "timeGridWeek" : "timeGridDay",
        initialDate: props.currentViewDate,
        firstDay: 1,
        slotMinTime: "05:00:00",
        slotMaxTime: "24:00:00",
        editable: true,
        eventDurationEditable: false,
        // weekNumberCalculation: "ISO",
        timeZone:"local",
        slotDuration: "00:30:00",
        weekNumbers: true,
        weekNumberTitle: "Uge",
        height: 800,
        allDaySlot: false,
        nowIndicator: true,
        displayEventEnd: true,
        headerToolbar:
            width >= 810
                ? {
                      left: "title",
                      center: "",
                      right: "addNewClientButton addCourseButton timeGridWeek dayGridMonth timeGridDay today prev next"
                  }
                : {
                      left: "title",
                      center: "",
                      right: "addNewClientButton addCourseButton timeGridWeek timeGridDay today prev next"
                  },

        customButtons: {
            addNewClientButton: {
                text: "Tilføj ny patient",
                click: () => history.push("/NewClientForm")
            },
            addCourseButton: {
                text: "Åbningstid",
                click: () => {
                    if (props.therapist.plan === "pro") {
                        props.setOpeningHour(true);
                    } else {
                        props.setPremiumAlert(true);
                    }
                }
            }
        },

        slotLabelFormat: { hour: "2-digit", minute: "2-digit", hour12: false },
        dayHeaderFormat: { weekday: "short", day: "numeric", month: "numeric" },
        buttonText: {
            today: "Hop til i dag",
            month: "Måned",
            week: "Uge"
        },
        locale: daLocale,
        events: function (info, successCallback, failureCallback) {
            fetchEvents(info, successCallback, failureCallback);
        },
        dateClick: props.handleDateClick,
        eventClick: props.handleEventClick,
        eventDrop: props.handleDrop,
        eventDidMount: info => {
            handleMouseOver(info.event.extendedProps, info.el);
        },
        dayHeaderContent: arg => {
            const color = (JSON.parse(props.therapist.colors) || ["", "#35408e"])[1];
            const isToday = arg.date.toDateString() === new Date().toDateString();

            const dayNumber = arg.text.split(" ")[1];
            const dayName = arg.text.split(" ")[0];

            return (
                <div>
                    {dayName}{" "}
                    {isToday ? (
                        <div className="blueCalendarCircle" style={{ backgroundColor: color }}>
                            {dayNumber}
                        </div>
                    ) : (
                        <div>{dayNumber}</div>
                    )}
                </div>
            );
        },
        datesSet: handleDatesSet,

    };

    return (
        <>
            <FullCalendar ref={props.calendarRef} {...calendarProps} />
            <ReactTooltip
                html={true}
                arrowColor="#FFFFFF"
                backgroundColor="#FFFFFF"
                textColor="#000000"
                place="right"
                type="light"
                borderColor="#808080"
                effect="solid"
            />
        </>
    );
});

export default CalendarComponent;
